import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CCol, CRow, CButton, CAlert, CSpinner, CCard, CCardBody, CBadge } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft } from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'

// Vista del Cobrador [RGM] 2026-10-05 (pedido Rafael: "al darle en la fila del
// cobrador debe llevarme al view"): mismo formato de la factura — azul
// institucional #078bcd + tarjetas .factura-panel — con datos, saldos
// (Faltantes/Vales/Balance SOS) y los 9 permisos del cobrador.
// GX: captions del WW cobradorww.cs:2554-2568 (CobradorFaltantes = "Balance
// SOS" :2561, CobradorVales = "Pagos SOS" :2562, CobradorBalanceSOS :2567).
const PRIMARY_BLUE = '#078bcd'

const CobradorViewPage: React.FC = () => {
  const { codigo } = useParams<{ codigo: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [cobrador, setCobrador] = useState<any>(null)
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    setCargando(true)
    // El listado ya trae todos los campos (CobradoresController GET — sin
    // GET /cobradores/{id}); se localiza la fila por código.
    api.get<any[]>(`/cobradores?empresa=${empresa}`)
      .then((rows) => setCobrador(
        (rows ?? []).find((r) => String(r.cobradorCodigo) === String(codigo)) ?? null))
      .catch(() => setCobrador(null))
      .finally(() => setCargando(false))
  }, [codigo, empresa])

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (!cobrador) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Cobrador</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/cobradores')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">Cobrador no encontrado</CAlert>
      </>
    )
  }

  const isYes = (v?: any) => String(v ?? '').trim() === 'Si'
  const label = (txt: string) => <div className="factura-etiqueta">{txt}</div>
  const permission = (label: string, activo: boolean) => (
    <div className="d-flex justify-content-between align-items-center py-1" style={{ fontSize: 13 }}>
      <span>{label}</span>
      <CBadge color={activo ? 'success' : 'secondary'}>{activo ? 'Si' : 'No'}</CBadge>
    </div>
  )

  return (
    <>
      <CRow className="mb-3">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h4 className="mb-0">Cobrador {cobrador.cobradorCodigo}</h4>
          <CButton color="secondary" variant="outline" onClick={() => navigate('/cobradores')}>
            <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
            Volver
          </CButton>
        </CCol>
      </CRow>

      <CCard
        className="shadow-sm"
        style={{ borderRadius: 8, maxWidth: 920, borderTop: `4px solid ${PRIMARY_BLUE}` }}
      >
        <CCardBody style={{ padding: '22px 26px' }}>
          {/* ── Encabezado ── */}
          <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3 flex-wrap gap-2">
            <div>
              <div className="fs-4 fw-bold" style={{ color: PRIMARY_BLUE }}>
                {cobrador.nombre || cobrador.usuario || 'Cobrador'}
              </div>
              <div className="text-body-secondary small">Usuario: {cobrador.usuario || '—'}</div>
            </div>
            <div className="text-end">
              <div className="fs-4 fw-bold" style={{ color: PRIMARY_BLUE }}>COBRADOR</div>
              <div>Codigo <span className="fw-semibold">{cobrador.cobradorCodigo}</span></div>
              <div className="text-body-secondary small">
                {cobrador.empresaNombre || cobrador.empresaCodigo || ''}
              </div>
            </div>
          </div>

          <div className="row g-3">
            {/* ── Datos ── */}
            <div className="col-md-6">
              <div className="factura-panel h-100">
                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>DATOS</h6>
                <div className="row g-1">
                  <div className="col-6">
                    {label('Usuario')}<div>{cobrador.usuario || '—'}</div>
                  </div>
                  <div className="col-6">
                    {label('Nombre')}<div>{cobrador.nombre || '—'}</div>
                  </div>
                  <div className="col-6">
                    {label('Calificacion')}<div>{cobrador.cobradorCalificacion ?? 0}</div>
                  </div>
                  <div className="col-6">
                    {label('Empresa')}<div>{cobrador.empresaNombre || cobrador.empresaCodigo || '—'}</div>
                  </div>
                  <div className="col-12">
                    {label('Geolocalizacion')}<div>{cobrador.cobradorGeolocalizacion || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Saldos (GX: Faltantes = "Balance SOS" :2561, Vales = "Pagos
                SOS" :2562; CobradorBalanceSOS es un campo aparte :2567) ── */}
            <div className="col-md-6">
              <div className="factura-panel h-100">
                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>SALDOS</h6>
                <div className="row g-1">
                  <div className="col-6">
                    {label('Faltantes')}<div className="fw-semibold">{fmtMoneda(cobrador.cobradorFaltantes)}</div>
                  </div>
                  <div className="col-6">
                    {label('Vales')}<div className="fw-semibold">{fmtMoneda(cobrador.cobradorVales)}</div>
                  </div>
                  <div className="col-6">
                    {label('Balance SOS')}<div className="fw-semibold">{fmtMoneda(cobrador.cobradorBalanceSOS)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Permisos (los 9 switches del cobrador) ── */}
            <div className="col-12">
              <div className="factura-panel">
                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>PERMISOS</h6>
                <div className="row g-1">
                  <div className="col-md-4">{permission('Hace renovación', isYes(cobrador.cobradorHaceRenovacion))}</div>
                  <div className="col-md-4">{permission('Crea cliente', isYes(cobrador.cobradorCreaCliente))}</div>
                  <div className="col-md-4">{permission('Hace cierre', isYes(cobrador.cobradorHaceCierre))}</div>
                  <div className="col-md-4">{permission('Modificar?', isYes(cobrador.cobradorBorrar))}</div>
                  <div className="col-md-4">{permission('Presta Diario', isYes(cobrador.cobradorPrestaDiario))}</div>
                  <div className="col-md-4">{permission('Presta Semanal', isYes(cobrador.cobradorPrestaSemanal))}</div>
                  <div className="col-md-4">{permission('Presta Quincenal', isYes(cobrador.cobradorPrestaQuincenal))}</div>
                  <div className="col-md-4">{permission('Presta Mensual', isYes(cobrador.cobradorPrestaMensual))}</div>
                  <div className="col-md-4">{permission('Presta Libre', isYes(cobrador.cobradorPrestaLibre))}</div>
                </div>
              </div>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

export default CobradorViewPage
